import type { ObservationScene, SceneElement } from '../types';
import { percentCenterRectStyle } from '../systems/clickable-screen-utils';
import {
  getAnomalyElements,
  getInspectedFlag,
  getReportedFlag,
  INSPECT_VERDICT_LABELS,
  isInspectObjectElement,
  resolveInspectBorderClasses,
  resolveInspectVerdictStatus,
  type InspectVerdictStatus,
} from '../systems/object-inspect-utils';
import styles from './observation-overlay.module.css';

const SVG_LOUPE = (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <circle cx="10.5" cy="10.5" r="6.5" />
    <path d="M20 20 L16 16" />
  </svg>
);

const SVG_SHIELD = (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.1" aria-hidden>
    <path d="M12 3 L20 6.5 V12 c0 5-3.4 7.6-8 9-4.6-1.4-8-4-8-9 V6.5 Z" />
  </svg>
);

const VERDICT_ICONS: Record<InspectVerdictStatus, JSX.Element | string> = {
  vp: SVG_SHIELD,
  fn: SVG_SHIELD,
  fp: '○',
  vn: '○',
};

const VERDICT_CLASS: Record<InspectVerdictStatus, string> = {
  vp: styles.stVp as string,
  fn: styles.stFn as string,
  fp: styles.stFp as string,
  vn: styles.stVn as string,
};

const BORDER_CLASS: Record<string, string> = {
  bLeft: styles.bLeft as string,
  bBottom: styles.bBottom as string,
};

interface ObservationOverlayProps {
  scene: ObservationScene;
  flags: Record<string, boolean>;
  instruction: string;
  exitPrompt: string;
  exitChoiceText: string;
  gateOpen: boolean;
  disabled?: boolean;
  inspectModalOpen?: boolean;
  evaluateCondition?: (condition: string) => boolean;
  onZoneClick: (elementId: string) => void;
  onExit: () => void;
}

function getElementRect(element: SceneElement) {
  const { x, y, width = 12, height = 12 } = element.position;
  return { x, y, width, height };
}

export function ObservationOverlay({
  scene,
  flags,
  instruction,
  exitPrompt,
  exitChoiceText,
  gateOpen,
  disabled = false,
  inspectModalOpen = false,
  evaluateCondition = () => true,
  onZoneClick,
  onExit,
}: ObservationOverlayProps): JSX.Element | null {
  const objects = (scene.elements ?? []).filter(
    (element) => isInspectObjectElement(element)
      && (!element.condition || evaluateCondition(element.condition)),
  );
  const anomalies = getAnomalyElements(scene);
  const anomalyCount = anomalies.length;
  const classifiedAnomalies = anomalies.filter(
    (element) => Boolean(flags[getInspectedFlag(element.id)]),
  ).length;

  if (!objects.length) return null;

  const zonesLocked = disabled || inspectModalOpen;

  const gateText = gateOpen
    ? (exitPrompt || 'Tous les objets à risque sont classés.')
    : anomalyCount - classifiedAnomalies > 0
      ? `Encore ${anomalyCount - classifiedAnomalies} objet(s) à risque à classer.`
      : 'Il reste des objets à risque à classer.';

  return (
    <div className={styles.overlay} aria-hidden={disabled}>
      <div className={styles.voile} aria-hidden />

      <div className={`${styles.hud} cn-zoom-ui`}>
        <div className={styles.hudGlass}>
          <span className={`${styles.mono} ${styles.monoOr}`}>Observation</span>
        </div>
        <div className={styles.hudGlass}>
          <span className={styles.mono}>Risques traités</span>
          <span className={styles.hudCount}>{classifiedAnomalies}</span>
          <span className={styles.mono}>sur {anomalyCount}</span>
        </div>
      </div>

      <div className={styles.zones}>
        {objects.map((element) => {
          const inspected = Boolean(flags[getInspectedFlag(element.id)]);
          const reported = Boolean(flags[getReportedFlag(element.id)]);
          const verdict = resolveInspectVerdictStatus(element, inspected, reported);
          const rect = getElementRect(element);
          const borderClasses = resolveInspectBorderClasses(rect);

          return (
            <button
              key={element.id}
              type="button"
              className={[
                styles.zone,
                ...borderClasses.map((cls) => BORDER_CLASS[cls]),
                inspected && verdict ? styles.corrige : '',
                verdict ? VERDICT_CLASS[verdict] : '',
              ].filter(Boolean).join(' ')}
              style={percentCenterRectStyle(rect)}
              disabled={zonesLocked || inspected}
              aria-label={inspected ? INSPECT_VERDICT_LABELS[verdict!] : 'Examiner'}
              onClick={(event) => {
                if (inspected || zonesLocked) return;
                event.stopPropagation();
                onZoneClick(element.id);
              }}
            >
              <span className={`${styles.corner} ${styles.cornerHg}`} aria-hidden />
              <span className={`${styles.corner} ${styles.cornerHd}`} aria-hidden />
              <span className={`${styles.corner} ${styles.cornerBg}`} aria-hidden />
              <span className={`${styles.corner} ${styles.cornerBd}`} aria-hidden />
              <span className={`${styles.coin} cn-zoom-ui`}>
                <span className={styles.coinIco}>{SVG_LOUPE}</span>
                <span className={styles.coinVerdict}>
                  {verdict ? VERDICT_ICONS[verdict] : null}
                </span>
                <em className={styles.coinLabel}>
                  {verdict ? INSPECT_VERDICT_LABELS[verdict] : 'examiner'}
                </em>
              </span>
            </button>
          );
        })}
      </div>

      <div className={`${styles.lame} cn-zoom-ui`}>
        <div className={styles.bulle}>
          <div className={styles.dit}>{instruction}</div>
        </div>
        <div className={styles.pied}>
          <div className={`${styles.gate} ${gateOpen ? styles.gateReady : ''}`}>
            <span className={`${styles.gatePt} ${gateOpen ? styles.gatePtReady : ''}`} aria-hidden />
            <span>{gateText}</span>
          </div>
        </div>
      </div>

      <button
        type="button"
        className={`${styles.continuer} cn-zoom-ui ${gateOpen ? styles.continuerPret : ''}`}
        disabled={!gateOpen || disabled}
        onClick={onExit}
      >
        {exitChoiceText} →
      </button>
    </div>
  );
}
